/**
 * Fitting the stat cards onto one row, at any width.
 *
 * The strip used to be `flex-wrap`, which is the honest CSS answer and the
 * wrong product answer: a second row of cards pushes the table below the fold,
 * and the table is what the page is for. So the strip never wraps. What gives
 * instead is the cards, in the order a person would give them up:
 *
 *   full → compact → flat → chip → and only then, drop from the right.
 *
 * `full` is the mockup. `compact` is the same card with its padding and its
 * footnote gone. `flat` drops the charts, because a 140px sparkline is the
 * most expensive thing on a card and the least of what it says. `chip` is the
 * irreducible card: a colour, a name and a number. Past that there is nothing
 * left to take off a card, so cards start leaving — from the right-hand end,
 * because the left-hand ones are the ones people put first.
 *
 * The "New card" tile never leaves. A strip you cannot add to is a strip that
 * gets narrower and stays that way.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export type CardDensity = 'full' | 'compact' | 'flat' | 'chip'

/** Widest first, so the first one that fits is the best one that fits. */
export const DENSITIES: CardDensity[] = ['full', 'compact', 'flat', 'chip']

/** What one thing in the strip needs, at each density. */
export type StripWidths = Record<CardDensity, number>

export interface StripFit {
  /** Put this on the row that holds the cards. */
  ref: (node: HTMLDivElement | null) => void
  density: CardDensity
  /** How many of `widths` to draw. The rest are dropped off the right. */
  visible: number
}

/**
 * The "+N" button that stands in for what was dropped.
 *
 * Counted only when something *is* dropped, which is the whole point: it was
 * not counted at all, so a strip that shed a card then drew a button it had
 * left no room for, and pushed the "New card" tile off the right-hand edge of
 * a phone. The tile is the one thing in here that must never be unreachable.
 */
const OVERFLOW = 36

/**
 * Measures the row and decides how much card there is room for.
 *
 * Takes each item's width at each density rather than measuring the rendered
 * cards, deliberately: measuring what is on screen means rendering it first,
 * and a card that renders wide and is then told to be narrow is a frame of
 * layout everybody can see.
 */
export function useStrip(widths: StripWidths[], tail: StripWidths, gap = 8): StripFit {
  const [available, setAvailable] = useState<number | null>(null)
  const node = useRef<HTMLDivElement | null>(null)

  const ref = useCallback((element: HTMLDivElement | null) => {
    node.current = element
    if (element) setAvailable(element.clientWidth)
  }, [])

  useEffect(() => {
    const element = node.current
    if (!element) return
    /*
     * jsdom and older Safari have no ResizeObserver. Falling back to the width
     * measured once is right rather than merely safe: the strip then behaves
     * as it did before this existed instead of collapsing to chips.
     */
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailable(entry.contentRect.width)
    })
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return { ref, ...fit(widths, tail, gap, available) }
}

/**
 * The decision itself, without the DOM — which is what makes it testable, and
 * why the hook above is four lines of plumbing around it.
 */
export function fit(
  widths: StripWidths[], tail: StripWidths, gap: number, available: number | null,
): { density: CardDensity; visible: number } {
  // Before the first measurement, draw everything at full size. Any other
  // guess flashes.
  if (available === null || available <= 0) {
    return { density: 'full', visible: widths.length }
  }

  const needed = (density: CardDensity, count: number): number => {
    const cards = widths.slice(0, count).reduce((sum, item) => sum + item[density], 0)
    // Room for the "+N" button, but only when there will be one.
    const dropped = count < widths.length ? OVERFLOW + gap : 0
    // One gap between every pair, and one before the tail.
    return cards + tail[density] + gap * count + dropped
  }

  for (const density of DENSITIES) {
    if (needed(density, widths.length) <= available) {
      return { density, visible: widths.length }
    }
  }

  // Nothing fits whole. Stay at the smallest and shed from the right.
  let visible = widths.length
  while (visible > 0 && needed('chip', visible) > available) visible -= 1
  return { density: 'chip', visible }
}
